import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState, Fragment } from "react";
import { InitialNameAvatar } from "../../../components/images/initial-name-avatar";
import { AppModalDialogBox } from "../../../components/modals";
import Paginator from "../../../components/mui/pagination";
import { PaginationContext } from "../../../components/mui/table/simple-table";
import { convertToFullName } from "../../../services/utils";
import { InitiativesExpandedRowData } from "./expanded-row-data";
import { INITIATIVES_GROUP_COLUMNS } from "./table-config";

function InitiativesListTable({
  list,
  columns = INITIATIVES_GROUP_COLUMNS,
  handlePageFilters,
  appliedFilters,
  onEditClick,
  onDeleteClick,
}: any) {
  const { selectedPage, selectedPageSize } = appliedFilters;
  const [summaryInitiative, setSummaryInitiative] = useState<any>(null);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const rows = list?.data || [];
  const dataColumns = columns.filter((column) => column.field !== "expandable");

  const toggleExpanded = (id: string) => {
    if (!id) return;
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const onPageSizeChange = (newValue) => {
    handlePageFilters({
      selectedPageSize: newValue,
      selectedPage: 1,
    });
  };

  const onPageChange = (newValue) => {
    handlePageFilters({
      selectedPageSize,
      selectedPage: newValue,
    });
  };

  const renderCell = (row, column) => {
    switch (column.rendererType) {
      case "AMBASSADOR": {
        const ambassadorName = convertToFullName(
          row.firstName,
          row.lastName
        ).trim();

        return (
          <InitialNameAvatar
            text={ambassadorName || "-"}
            name={row.firstName || "-"}
          />
        );
      }
      case "PROJECT_COUNT":
        return (
          <Chip
            size="small"
            label={row.projectCount ?? row.projects?.length ?? 0}
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: "#F3F4F6",
              color: "#374151",
            }}
          />
        );
      default:
        return row?.[column.field] || "-";
    }
  };

  return (
    <>
      <Box>
        <TableContainer
          sx={{
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            border: "1px solid #E5E7EB",
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 36,
                    maxWidth: 36,
                    padding: "12px 0 12px 8px",
                    backgroundColor: "#F8FAFC",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                />
                {dataColumns.map((column, index) => (
                  <TableCell
                    key={`head-${column.field}-${index}`}
                    align={column.align}
                    sx={{
                      backgroundColor: "#F8FAFC",
                      color: "#374151",
                      fontWeight: 600,
                      fontSize: "14px",
                      padding: index === 0 ? "12px 16px 12px 4px" : "12px 16px",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                  >
                    {column.title}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const rowId = row.id || String(index);
                const isExpanded = Boolean(expandedIds[rowId]);

                return (
                  <Fragment key={rowId}>
                    <TableRow
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "#FAFAFA" },
                        "&:hover": { backgroundColor: "#F9FAFB" },
                      }}
                    >
                      <TableCell
                        sx={{
                          width: 36,
                          maxWidth: 36,
                          padding: "12px 0 12px 8px",
                          borderBottom: isExpanded
                            ? "none"
                            : "1px solid #F3F4F6",
                        }}
                      >
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => toggleExpanded(rowId)}
                          sx={{ color: "#6B7280", padding: "4px" }}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      {dataColumns.map((column, columnIndex) => (
                        <TableCell
                          key={`cell-${rowId}-${column.field}`}
                          align={column.align}
                          sx={{
                            fontSize: "14px",
                            color: "#1F2937",
                            padding:
                              columnIndex === 0
                                ? "12px 16px 12px 4px"
                                : "12px 16px",
                            borderBottom: isExpanded
                              ? "none"
                              : "1px solid #F3F4F6",
                            verticalAlign: "middle",
                          }}
                        >
                          {renderCell(row, column)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={dataColumns.length + 1}
                          sx={{
                            paddingBottom: 0,
                            paddingTop: 0,
                            borderBottom: "1px solid #F3F4F6",
                          }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <InitiativesExpandedRowData
                              projects={row.projects}
                              onEditClick={onEditClick}
                              onDeleteClick={onDeleteClick}
                              onSummaryClick={setSummaryInitiative}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > 0 && (
          <Box mt={1.5}>
            <PaginationContext.Provider
              value={{
                onPageChange,
                onPageSizeChange,
                totalRecords: list?.total,
                showPageSize: true,
                selectedPageSize,
                selectedPage,
              }}
            >
              <Paginator />
            </PaginationContext.Provider>
          </Box>
        )}
      </Box>

      {summaryInitiative && (
        <AppModalDialogBox
          isModalOpen
          title={summaryInitiative.title || "Summary"}
          handleClose={() => setSummaryInitiative(null)}
          size="md"
          actionButtonRenderer={
            <Button onClick={() => setSummaryInitiative(null)}>Close</Button>
          }
        >
          <Typography
            fontSize={14}
            color="#374151"
            whiteSpace="pre-wrap"
            lineHeight={1.6}
          >
            {summaryInitiative.summary?.trim() || "No summary available."}
          </Typography>
        </AppModalDialogBox>
      )}
    </>
  );
}

export { InitiativesListTable };
